import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router-dom';
import {
  Award,
  ChevronDown,
  Clapperboard,
  ExternalLink,
  Globe,
  LifeBuoy,
  Menu,
  Moon,
  LogOut,
  MessageCircleQuestion,
  Shield,
  Sun,
  User,
  KeyRound,
  Pencil,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth';
import {
  useAccessibleMemberAreas,
  useCourseBySlug,
  usePublicGamificationSettings,
  usePublicMenuItems,
} from '@/hooks';
import { NotificationDropdown } from '@/components/ui/NotificationDropdown';
import { MemberPopup } from '@/components/popups/MemberPopup';
import { CommunicationActivity } from '@/components/communications/CommunicationActivity';
import { GamifiedAvatar } from '@/components/ui/GamifiedAvatar';
import { WhatsAppBubble } from '@/components/ui/WhatsAppBubble';
import { CarregandoPagina } from '@/components/ui/CarregandoPagina';
import { Button } from '@/components/ui/button';
import { Atmosfera } from '@/components/auth/Atmosfera';
import { CampoDeCruzes } from '@/components/auth/CampoDeCruzes';
import {
  BrandPauseMenuItem,
  RotatingBrand,
  StaticBrand,
} from '@/components/brand/RotatingBrand';
import type { EcosystemBrand } from '@/components/brand/BrandMark';
import { ecosystemKey } from '@/lib/member-home';
import { SiteBannerCarousel } from '@/components/student/SiteBannerCarousel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { devflixService } from '@/services/devflix.service';
import '@/styles/member-home.css';
import { usePlatformTheme } from '@/hooks/usePlatformTheme';
import styles from './HomeLayout.module.css';

const SUPPORT_URL = 'https://go.rodolfomori.com.br/suporte';
const COMMUNITY_URL =
  'https://comunidade.devclub.com.br/users/sign_in?post_login_redirect=https%3A%2F%2Fcomunidade.devclub.com.br%2F';

const COMPACT_MENU_QUERY = '(max-width: 900px)';

function subscribeToCompactMenu(notify: () => void) {
  const query = window.matchMedia(COMPACT_MENU_QUERY);
  query.addEventListener('change', notify);
  return () => query.removeEventListener('change', notify);
}

function isCompactMenu() {
  return window.matchMedia(COMPACT_MENU_QUERY).matches;
}

function readBubblePreference(userId?: string): boolean {
  try {
    return (
      !userId || localStorage.getItem(`whatsapp-bubble-${userId}`) !== 'false'
    );
  } catch {
    return true;
  }
}

function validMenuUrl(url?: string): url is string {
  return !!url && (/^\/(?!\/)/.test(url) || /^https?:\/\//i.test(url));
}

/** Translate the old CMS route names without changing stored menu content. */
function normalizeMenuUrl(url?: string): string | undefined {
  if (!url?.startsWith('/') || url.startsWith('//')) return url;
  const [pathname] = url.split(/[?#]/, 1);
  const aliases: Record<string, string> = {
    '/cursos': '/courses',
    '/perfil': '/profile',
    '/certificados': '/certificates',
    '/dashboard': '/home',
  };
  const target = aliases[pathname.replace(/\/$/, '')];
  return target ? target + url.slice(pathname.length) : url;
}

function isBuiltInMenuTitle(title: string): boolean {
  const normalized = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  return ['comunidade', 'centraldeajuda', 'meuscursos', 'minhasformacoes', 'suasformacoes'].includes(normalized);
}

function FormationHeaderBrand({
  courseSlug,
  areaId,
  ecosystem,
}: {
  courseSlug?: string;
  areaId?: string | null;
  ecosystem?: EcosystemBrand;
}) {
  const course = useCourseBySlug(courseSlug || '', !!courseSlug);
  const areas = useAccessibleMemberAreas(!!courseSlug || !!areaId);
  let brand: EcosystemBrand | undefined;

  if (
    !areas.isLoading && !areas.isError && areas.data &&
    (!courseSlug || (!course.isLoading && !course.isError && course.data))
  ) {
    const parentAreaId = courseSlug ? course.data?.memberAreaId : areaId;
    if (parentAreaId) {
      const parent = areas.data.find((area) => area.id === parentAreaId);
      brand = parent ? ecosystemKey(parent.slug) : undefined;
    } else if (courseSlug && course.data) {
      // Cursos compartilhados podem não ter uma área principal cadastrada.
      const matchingAreas = areas.data.filter((area) =>
        area.courses?.some((item) => item.id === course.data?.id),
      );
      const context = matchingAreas.find((area) => area.id === areaId) ??
        (ecosystem ? matchingAreas.find((area) => ecosystemKey(area.slug) === ecosystem) : undefined);
      const brands = new Set(matchingAreas.map((area) => ecosystemKey(area.slug)));
      brand = context
        ? ecosystemKey(context.slug)
        : brands.size === 1 ? [...brands][0] : undefined;
    }
  }

  return <StaticBrand brand={brand} className={styles.brand} />;
}

function ContextualHeaderBrand() {
  const { pathname, search } = useLocation();
  const course = useMatch('/courses/:slug/*');
  const area = useMatch('/areas/:areaId');
  const params = new URLSearchParams(search);
  const catalog = /^\/courses\/?$/.test(pathname);
  const trails = /^\/trilhas\/?$/.test(pathname);

  if (course?.params.slug) {
    return <FormationHeaderBrand courseSlug={course.params.slug} areaId={params.get('area')} ecosystem={ecosystemKey(params.get('ecosystem') || '')} />;
  }
  if (area || (catalog && params.has('area'))) {
    return <FormationHeaderBrand areaId={area?.params.areaId ?? params.get('area')} />;
  }
  const brand = /^\/works-ai\/?$/.test(pathname) ? 'works'
    : catalog || trails ? ecosystemKey(params.get('ecosystem') || '') : undefined;
  return <StaticBrand brand={brand} className={styles.brand} />;
}

/** Persistent navigation shared by the HOME, member pages and lessons. */
export function MemberLayout({ children }: { children?: ReactNode }) {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = usePlatformTheme();
  const { pathname, search, hash } = useLocation();
  const isHome = /^\/home\/?$/.test(pathname);
  const [devflixLoading, setDevflixLoading] = useState(false);
  const compactMenu = useSyncExternalStore(subscribeToCompactMenu, isCompactMenu);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [compactMenu, pathname, search, hash]);
  const {
    data: menuItems,
    isLoading: menuLoading,
    isError: menuError,
  } = usePublicMenuItems();
  const { data: publicSettings } = usePublicGamificationSettings();
  const settings = publicSettings as
    | { data?: { enabled?: boolean }; enabled?: boolean }
    | undefined;
  const gamificationEnabled = !!(settings?.data || settings)?.enabled;
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const extraMenuItems = useMemo(
    () =>
      (Array.isArray(menuItems) ? menuItems : [])
        .map((item) => ({ ...item, url: normalizeMenuUrl(item.url) }))
        .filter((item) => item.isActive !== false && validMenuUrl(item.url))
        .filter((item) => !isBuiltInMenuTitle(item.title))
        .filter(
          (item) =>
            !['/home', '/courses', '/profile', COMMUNITY_URL].includes(
              item.url!,
            ),
        )
        .sort((a, b) => a.order - b.order),
    [menuItems],
  );

  function currentPage(url: string): 'page' | undefined {
    if (!url.startsWith('/') || url.startsWith('//')) return undefined;
    const target = new URL(url, window.location.origin);
    return pathname.replace(/\/$/, '') === target.pathname.replace(/\/$/, '') &&
      (!target.search || target.search === search) &&
      (!target.hash || target.hash === hash)
      ? 'page'
      : undefined;
  }

  function openDevflix() {
    setMenuOpen(false);
    setDevflixLoading(true);
    try {
      devflixService.open();
    } catch {
      setDevflixLoading(false);
      toast.error('Não foi possível abrir a DevFlix agora. Tente novamente.');
    }
  }

  return (
    <div className={styles.layout} data-theme={theme}>
      <div className={styles.ambient} aria-hidden="true">
        <Atmosfera />
        <CampoDeCruzes />
      </div>
      <a className={styles.skipLink} href="#member-main">
        Ir para o conteúdo
      </a>

      <header
        className={`member-home-scope ${styles.header}`}
        data-tour="header"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && menuOpen && !event.defaultPrevented) {
            event.preventDefault();
            setMenuOpen(false);
            menuToggleRef.current?.focus();
          }
        }}
      >
        <div className={styles.headerInner}>
          {isHome ? <RotatingBrand className={styles.brand} /> : <ContextualHeaderBrand />}

          {compactMenu && (
            <Button
              ref={menuToggleRef}
              type="button"
              variant="ghost"
              size="icon"
              className={styles.menuToggle}
              aria-label={menuOpen ? 'Fechar menu principal' : 'Abrir menu principal'}
              aria-expanded={menuOpen}
              aria-controls="member-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
            </Button>
          )}

          <nav
            id="member-navigation"
            className={styles.navigation}
            aria-label="Navegação principal"
            hidden={compactMenu && !menuOpen}
            onClick={(event) => {
              if (event.target instanceof Element && event.target.closest('a')) {
                setMenuOpen(false);
              }
            }}
          >
            {isAdmin && (
              <Link to="/admin/menus" className={styles.moreButton} aria-label="Editar menu geral" title="Editar menu geral">
                <Pencil size={16} aria-hidden="true" />
                <span className={styles.mobileLabel}>Editar menu geral</span>
              </Link>
            )}
            <Link to="/home" className={styles.navLink} aria-current={pathname === '/home' && !hash ? 'page' : undefined}>
              Início
            </Link>
            <Link to="/home#continuar-assistindo" className={styles.navLink} aria-current={currentPage('/home#continuar-assistindo')}>
              Continuar assistindo
            </Link>
            <Link to="/trilhas" className={styles.navLink} aria-current={pathname === '/trilhas' || /^\/courses\/[^/]+\/trail\/?$/.test(pathname) ? 'page' : undefined}>Trilhas</Link>
            <Link to="/works-ai" className={styles.navLink} aria-current={currentPage('/works-ai')}>Works AI</Link>
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.navLink}
              aria-label="Comunidade (abre em nova aba)"
            >
              Comunidade
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={styles.moreButton}>
                  Mais{' '}
                  <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={`member-home-scope ${styles.dropdown}`}
                data-theme={theme}
                align="end"
                sideOffset={8}
              >
                {menuLoading && (
                  <DropdownMenuItem disabled>
                    Carregando seus links…
                  </DropdownMenuItem>
                )}
                {menuError && (
                  <p className={styles.menuNotice}>
                    Os links adicionais estão indisponíveis no momento.
                  </p>
                )}
                {extraMenuItems.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    asChild
                    className={styles.menuItem}
                  >
                    {item.isExternal ||
                    item.openNewTab ||
                    /^https?:\/\//i.test(item.url!) ? (
                      <a
                        href={item.url}
                        aria-current={currentPage(item.url!)}
                        target={item.openNewTab ? '_blank' : undefined}
                        rel={
                          item.openNewTab ? 'noopener noreferrer' : undefined
                        }
                      >
                        <Globe size={17} aria-hidden="true" />
                        <span>{item.title}</span>
                        {item.openNewTab && (
                          <ExternalLink size={13} aria-hidden="true" />
                        )}
                      </a>
                    ) : (
                      <Link to={item.url!} aria-current={currentPage(item.url!)}>
                        <Globe size={17} aria-hidden="true" />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
                {!menuLoading &&
                  !extraMenuItems.some((item) => item.url === '/ranking') &&
                  gamificationEnabled && (
                    <DropdownMenuItem asChild className={styles.menuItem}>
                      <Link to="/ranking" aria-current={currentPage('/ranking')}>
                        <Award size={17} aria-hidden="true" />
                        Ranking
                      </Link>
                    </DropdownMenuItem>
                  )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className={styles.menuItem}
                  onSelect={openDevflix}
                  disabled={devflixLoading}
                >
                  <Clapperboard size={17} aria-hidden="true" />
                  <span>
                    {devflixLoading ? 'Abrindo DevFlix…' : 'Cursos gratuitos'}
                  </span>
                  <small>DevFlix</small>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className={styles.menuItem}>
                  <a
                    href={SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LifeBuoy size={17} aria-hidden="true" />
                    Central de ajuda
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && (
              <Button asChild variant="ghost" size={compactMenu ? 'default' : 'icon'} className={styles.navAction}>
                <Link to="/admin" aria-label="Abrir painel administrativo" data-tour="header-admin">
                  <Shield size={17} aria-hidden="true" />
                  <span className={styles.mobileLabel}>Painel administrativo</span>
                </Link>
              </Button>
            )}
            <Button asChild variant="ghost" size={compactMenu ? 'default' : 'icon'} className={styles.navAction}>
              <a href={SUPPORT_URL} aria-label="Suporte" data-tour="header-support" target="_blank" rel="noopener noreferrer">
                <LifeBuoy size={17} aria-hidden="true" />
                <span className={styles.mobileLabel}>Suporte</span>
              </a>
            </Button>
          </nav>

          <div className={styles.controls}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
              title={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
            </Button>
            <div className={styles.notifications} data-tour="header-notifications">
              <NotificationDropdown />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size={compactMenu ? 'icon' : 'default'}
                  className={styles.accountButton}
                  aria-label="Abrir menu da conta"
                  data-tour="header-avatar"
                >
                  <GamifiedAvatar
                    src={user?.avatar}
                    name={user?.name}
                    size="sm"
                  />
                  <span className={styles.accountName}>
                    {user?.name?.split(' ')[0]}
                  </span>
                  <ChevronDown size={13} strokeWidth={1.5} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={`member-home-scope ${styles.dropdown}`}
                data-theme={theme}
                align="end"
                sideOffset={8}
              >
                <div className={styles.accountInfo}>
                  <strong>{user?.name}</strong>
                  <span>{user?.email}</span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className={styles.menuItem}>
                  <Link to="/profile" aria-current={pathname === '/profile' && new URLSearchParams(search).get('aba') !== 'acessos' ? 'page' : undefined}>
                    <User size={17} aria-hidden="true" />
                    Meu perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className={styles.menuItem}>
                  <Link to="/profile?aba=acessos" aria-current={pathname === '/profile' && new URLSearchParams(search).get('aba') === 'acessos' ? 'page' : undefined}>
                    <KeyRound size={17} aria-hidden="true" />
                    Meus acessos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className={styles.menuItem}>
                  <Link to="/certificates" aria-current={currentPage('/certificates')}>
                    <Award size={17} aria-hidden="true" />
                    Certificados
                  </Link>
                </DropdownMenuItem>
                {user?.role === 'STUDENT' && <DropdownMenuItem asChild className={styles.menuItem}>
                  <Link to="/communication-preferences" aria-current={currentPage('/communication-preferences')}>
                    <MessageCircleQuestion size={17} aria-hidden="true" />
                    Preferências de comunicação
                  </Link>
                </DropdownMenuItem>}
                {isAdmin && (
                  <DropdownMenuItem asChild className={styles.menuItem}>
                    <Link to="/admin" aria-current={currentPage('/admin')}>
                      <Shield size={17} aria-hidden="true" />
                      Painel administrativo
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.role === 'INSTRUCTOR' && (
                  <DropdownMenuItem asChild className={styles.menuItem}>
                    <Link to="/admin/duvidas" aria-current={currentPage('/admin/duvidas')}>
                      <MessageCircleQuestion size={17} aria-hidden="true" />
                      Dúvidas dos alunos
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className={styles.menuItem} onSelect={toggleTheme}>
                  {theme === 'dark' ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
                  {theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
                </DropdownMenuItem>
                {isHome && <BrandPauseMenuItem className={styles.menuItem} />}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className={`${styles.menuItem} ${styles.logout}`}
                  onSelect={logout}
                >
                  <LogOut size={17} aria-hidden="true" />
                  Sair da conta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <Suspense fallback={<main id="member-main" tabIndex={-1} className={styles.main} aria-busy="true"><CarregandoPagina /></main>}>
        {children ?? <Outlet />}
      </Suspense>
      <MemberPopup />
      <CommunicationActivity />
    </div>
  );
}

/** HOME content; course navigation remains in StudentLayout. */
export function HomeLayout({ children }: { children?: ReactNode }) {
  const { user } = useAuthStore();
  const { theme } = usePlatformTheme();
  const { pathname, hash, key: locationKey } = useLocation();
  const [showBubble, setShowBubble] = useState(() =>
    readBubblePreference(user?.id),
  );

  useLayoutEffect(() => {
    const previousTitle = document.title;
    document.title = 'DevClub — Área de membros';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setShowBubble(readBubblePreference(user?.id));
  }, [user?.id]);

  useEffect(() => {
    if (pathname !== '/home' || hash !== '#continuar-assistindo') return;
    const frame = requestAnimationFrame(() => {
      document.getElementById('continuar-assistindo')?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, locationKey]);

  return (
    <div className="member-home-scope" data-theme={theme}>
      <main id="member-main" className={styles.main} tabIndex={-1}>
        {children ?? <Outlet />}
        <SiteBannerCarousel position="header" className={styles.campaign} />
      </main>
      {showBubble && <WhatsAppBubble />}
    </div>
  );
}
